// Custom fetch helpers for the invoice routes that the standard api() client
// cannot serve: they signal distinct preconditions via a 409 JSON body of
// shape `{ error, reason }` — api() collapses this to a thrown `Error(error)`
// and discards the structured `reason`, which the user-facing toast mapping
// needs (Gate 4 C5 §3.6). 179 C.3 — the two binary-XML helpers (the v2.1 push
// and the UBL re-download) that were reason (a) for this file are gone with
// their routes.

import { API_URL, getToken } from "../api";
import { srvErrText } from "./srvErr";
import { tImperative as _tImp } from "../i18n";

// brief 38 §3 — this file has its own `fetch`, so it never passes the seam in
// api.ts where the `srvErr.<code>` lookup runs. One helper, applied at every
// exit that turns a response body into a message, rather than sixteen edits
// that could disagree with each other. `srvErrText` returns null unless the
// seeded row IS this message in another language, so the wire text still wins
// wherever it says more than the code's label does.
const errText = (body: { error?: string } | null, res: Response) =>
  srvErrText(body) || body?.error || `HTTP ${res.status}`;

// 179 C.3 — the push-reason map and its resolver STOOD HERE: the five refusal
// reasons of the removed v2.1 push (`/submit`). Their rows leave the seed.
// 59 §3's [key, fallback] shape lives on in the trash map below.

// 179 C.2 — `TRANSITION_REASON_TO_KEY` / `transitionReasonToMessage` STOOD
// HERE: the four reasons of the removed PUT /:id/status route, imported by
// the modal and never called. Their seed rows leave with them.

// Gate 4 C4 — trash / restore error messages (179 C.3: the two billitOrderId
// reasons left with their route). brief 40
// §5: the words left this map for the dictionary. Each server `reason` binds
// to a key plus the sentence this file always carried — French, and the same
// French for every reader until now — which rides as the fallback so an
// unseeded box renders byte-for-byte what it always did. The resolver reads
// the row in the viewer's language at call time (a module-load read would
// freeze the login language).
const TRASH_REASON_TO_KEY: Record<string, [string, string]> = {
  "cannot-trash-pushed": [
    "proforma.trash_reason.cannot_trash_pushed",
    "⚠ Impossible de supprimer : la facture a été envoyée à Peppol.",
  ],
  "already-deleted": [
    "proforma.trash_reason.already_deleted",
    "⚠ Cette facture est déjà dans la corbeille.",
  ],
  "rp-or-admin-only": [
    "proforma.trash_reason.rp_or_admin_only",
    "⚠ Seul l’associé responsable ou un administrateur peut supprimer cette facture.",
  ],
  "not-deleted": [
    "proforma.trash_reason.not_deleted",
    "⚠ Cette facture n’est pas dans la corbeille.",
  ],
};

export function trashReasonToMessage(reason: string) {
  const hit = TRASH_REASON_TO_KEY[reason];
  return hit ? _tImp(hit[0], { fallback: hit[1] }) : null;
}

// 179 C.2 — `putInvoiceStatus` (PUT /:id/status, route removed with its
// matrix) and `postSendProForma` (a bare POST to send-pro-forma that nothing
// called; `sendProFormaToClient` below is the live one) STOOD HERE.

// Phase 35 (C2) — "Send to client". Same route as postSendProForma but with a
// payload: the client-rendered PDF (base64), the client email, and the email
// subject/body. The server archives the PDF into the case folder + drafts the
// Outlook email with it attached (both best-effort) and stamps the lock
// (authoritative). Returns { ok, invoice, sendResult } — sendResult carries
// { folderSaved, draftCreated, draftWebLink, stubbed, graphError } for toasts.
export async function sendProFormaToClient(invoiceId: string | number, payload?: any) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/send-pro-forma`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-LexCRM": "1",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload || {}),
  });
  if (res.ok) {
    const invoice = await safeJson(res);
    return { ok: true, invoice, sendResult: invoice?.sendResult || null };
  }
  const body = await safeJson(res);
  return {
    ok: false,
    status: res.status,
    reason: body?.reason || null,
    error: errText(body, res),
    body: body as unknown, // 209 §5 — a refusal's envelope (the language refusal names its matter)
  };
}

// Phase 35 Stage-3 r2 (§3) — POST /:id/send-to-billit (scaffold): sets To
// Peppol = Sent. Returns { ok, invoice }. 179 C.3 — the `ublGenerated` flag is
// gone: the server's UBL attempt never once succeeded and was removed.
export async function sendToBillit(invoiceId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/send-to-billit`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) {
    const invoice = await safeJson(res);
    return { ok: true, invoice };
  }
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// Phase 40 — POST /:id/generate-proforma. Server-side generate (merge → Graph
// PDF), save to client 04. FACTU (overwrite), and return that PDF for preview.
// Optional payload.language overrides the Client.language default. Returns
// { ok, pdfBase64, webUrl, fileName, language } | { ok:false, status, code, error }.
export async function generateProForma(invoiceId: string | number, payload?: any) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/generate-proforma`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload || {}),
  });
  if (res.ok) {
    const r = await safeJson(res);
    return { ok: true, pdfBase64: r?.pdfBase64 || null, webUrl: r?.webUrl || null, fileName: r?.fileName || null, language: r?.language || null, xml: r?.xml || null };
  }
  const body = await safeJson(res);
  return { ok: false, status: res.status, code: body?.code || null, error: errText(body, res), body: body as unknown }; // 209 §5 — the envelope rides
}

// Phase 40 (D9-REVISED) — GET /:id/factu-pdf. List every immutable FACTU version
// of this pro forma (newest-first) for "Show PDF" + the Generate-vs-Show
// decision. Returns { ok, exists, versions:[{version,fileName,webUrl}],
// latestWebUrl } | { ok:false, status, code, error }.
export async function getFactuPdf(invoiceId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/factu-pdf`, {
    method: "GET",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) {
    const r = await safeJson(res);
    return { ok: true, exists: !!r?.exists, versions: Array.isArray(r?.versions) ? r.versions : [], latestWebUrl: r?.latestWebUrl || null };
  }
  const body = await safeJson(res);
  return { ok: false, status: res.status, code: body?.code || null, error: errText(body, res) };
}

// Phase 35 Stage-3 r2 (§1) — PUT /:id. Edit an UN-SENT pro forma (same number +
// entries; the server lock-tier permits all fields while un-sent). Returns
// { ok, invoice } | { ok:false, status, reason, error }.
export async function editProForma(invoiceId: string | number, payload?: any) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload || {}),
  });
  if (res.ok) return { ok: true, invoice: await safeJson(res) };
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// Phase 35 Stage-3 — POST /:id/cancel. Active pro forma → cancelled; releases
// its entries to the billable pool. Returns { ok, invoice } | { ok:false, ... }.
export async function cancelProForma(invoiceId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/cancel`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) return { ok: true, invoice: await safeJson(res) };
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// Phase 35 Stage-3 — POST /:id/uncancel. Cancelled pro forma → active; re-binds
// its snapshot entries (skips any re-billed elsewhere). Returns
// { ok, invoice, rebound, skipped[], missing[] } | { ok:false, ... }.
// 179 C.1b — `{ dryRun: true }` asks for the PLAN and writes nothing: the
// served body then carries `rebound`, `skipped: [{ id, onInvoice }]`,
// `missing`, `oldTotal`, `newTotal` (the real call carries the same five
// beside the reshaped row).
export async function uncancelProForma(invoiceId: string | number, opts?: { dryRun?: boolean }) {
  const token = getToken();
  const suffix = opts?.dryRun ? "?dryRun=1" : "";
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/uncancel${suffix}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) {
    const invoice = await safeJson(res);
    return { ok: true, invoice, rebound: invoice?.rebound ?? 0, skipped: invoice?.skipped || [], missing: invoice?.missing || [] };
  }
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// 179 B.7 — POST /:id/approve (B.3). The approval act; the server decides
// (can() on the approve right, then the four state refusals) and the modal
// refetches. Returns { ok, invoice } | { ok:false, status, reason, error }.
// Written warning-free (typed reads of the untyped JSON, `??`, `String()` in
// the template): this file is in the eslint baseline and may not rise.
export async function approveProForma(invoiceId: string | number) {
  const token = getToken();
  const res = await globalThis.fetch(`${API_URL}/invoices/${String(invoiceId)}/approve`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) return { ok: true as const, invoice: (await safeJson(res)) as Record<string, unknown> | null };
  const body = (await safeJson(res)) as { error?: string; reason?: string | null } | null;
  return { ok: false as const, status: res.status, reason: body?.reason ?? null, error: errText(body, res) };
}

// Phase 35 Stage-3 — POST /:id/payments. Record a payment via the dedicated
// endpoint (NOT the lock-gated generic PUT). Returns { ok, invoice } | error.
export async function recordInvoicePayment(invoiceId: string | number, payment?: any) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/payments`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payment || {}),
  });
  if (res.ok) return { ok: true, invoice: await safeJson(res) };
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// 160 §1 — DELETE /:id/payments/:pid. Takes back ONE recorded payment on an
// UNSENT pro forma; `pid` is the Payment ROW's id, which the served entries
// carry (server/src/lib/shapes.js structuralShapeInvoice) when the row store
// answered. Returns the reshaped invoice, so the caller applies it exactly as
// it applies a record. 409 `invoice.cannot_remove_payment_sent` on a sent pro
// forma — that one is history and can only be cancelled.
export async function deleteInvoicePayment(invoiceId: string | number, paymentId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/payments/${paymentId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) return { ok: true, invoice: await safeJson(res) };
  const body = await safeJson(res);
  return { ok: false, status: res.status, reason: body?.reason || null, error: errText(body, res) };
}

// 179 C.2 — `postSubmit` (POST /:id/submit, the v2.1 push, no caller) STOOD
// HERE; the route itself goes in C.3.

// 179 C.3 — the UBL re-download helper, the download-response parser, its
// text fallback and the filename parser STOOD HERE: all served the removed push.

async function safeJson(res: Response) {
  try {
    return await res.clone().json();
  } catch {
    return null;
  }
}

// 179 C.2 — `getTrashedInvoices` (GET /invoices?trash=1, no caller) STOOD HERE.

// Gate 4 C4 — POST /api/invoices/:id/restore. Server returns 404, 409
// (reason: not-deleted), or the shaped invoice on success.
export async function postRestoreInvoice(invoiceId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}/restore`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.ok) {
    const invoice = await safeJson(res);
    return { ok: true, invoice };
  }
  const body = await safeJson(res);
  return {
    ok: false,
    status: res.status,
    reason: body?.reason || null,
    error: errText(body, res),
  };
}

// Gate 4 C4 — DELETE /api/invoices/:id (soft-delete / trash). Server
// returns 404 / 403 (rp-or-admin-only) / 409 (already-deleted,
// cannot-trash-pushed) / 204 on success.
export async function softDeleteInvoice(invoiceId: string | number) {
  const token = getToken();
  const res = await fetch(`${API_URL}/invoices/${invoiceId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-LexCRM": "1", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.status === 204) return { ok: true };
  const body = await safeJson(res);
  return {
    ok: false,
    status: res.status,
    reason: body?.reason || null,
    error: errText(body, res),
  };
}

// 179 C.3 — the billit-order-id PUT helper (route removed) and the blob
// download helper (its only caller was the UBL button) STOOD HERE.
